import React, { useState, useMemo } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Typography, Fade } from '@material-ui/core';

import { ChoiceOption } from '../types';
import { useTemplateParser } from '../hooks/ImpactAiContextProvider';
import { generateColorPaletteSet } from '../../../utils/colorHelpers';

interface ChoiceSelectorProps {
  choices: Record<string, ChoiceOption>;
  onSelect: (choiceKey: string, choice: ChoiceOption) => void;
  className?: string;
}

const useBaseStyles = makeStyles((theme: Theme) => ({
  choiceContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0),
    width: '100%',
  },
  choiceOption: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(2),
    cursor: 'pointer',
    transition: theme.transitions.create(['filter', 'transform'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      filter: 'brightness(0.9)',
      transform: 'translateY(-2px)',
    },
  },
  choiceText: {
    fontWeight: 500,
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.pxToRem(16),
  },
  icon: {
    fontSize: theme.typography.pxToRem(22),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const useDynamicStyles = (
  enhancedColors: Record<string, { backgroundColor: string; textColor: string; iconColor: string }>
) =>
  makeStyles(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dynamicStyles: Record<string, any> = {};

    Object.keys(enhancedColors).forEach((key) => {
      const colors = enhancedColors[key];
      dynamicStyles[`choiceOption-${key}`] = {
        backgroundColor: colors?.backgroundColor || '#F6F4FB',
      };
      dynamicStyles[`choiceIcon-${key}`] = {
        color: colors?.iconColor || 'inherit',
      };
      dynamicStyles[`choiceText-${key}`] = {
        color: colors?.textColor || 'inherit',
      };
    });

    return dynamicStyles;
  })();

const ChoiceSelector: React.FC<ChoiceSelectorProps> = ({ choices, onSelect, className }) => {
  const [visible, setVisible] = useState(true);
  const parseTemplate = useTemplateParser();

  // Convert Record to array for color generation, then back to lookup
  const choicesArray = Object.values(choices);
  const choiceKeys = Object.keys(choices);

  const enhancedColors = useMemo(() => {
    const colors = generateColorPaletteSet(choicesArray.length);
    return choiceKeys.reduce((acc, key, index) => {
      acc[key] = colors[index];
      return acc;
    }, {} as Record<string, { backgroundColor: string; textColor: string; iconColor: string }>);
  }, [choicesArray, choiceKeys]);

  const baseClasses = useBaseStyles();
  const dynamicClasses = useDynamicStyles(enhancedColors);

  const handleChoiceSelect = (key: string, choice: ChoiceOption) => {
    setVisible(false);
    setTimeout(() => {
      onSelect(key, choice);
    }, 300);
  };

  return (
    <Fade in={visible} timeout={300} mountOnEnter unmountOnExit>
      <div className={`${baseClasses.choiceContainer} ${className || ''}`}>
        {Object.entries(choices).map(([key, choice]) => {
          // Parse any template variables in the choice text
          const parsedText = parseTemplate(choice.text);

          return (
            <div
              key={key}
              className={`${baseClasses.choiceOption} ${
                dynamicClasses[`choiceOption-${key}`] || ''
              }`}
              onClick={() => handleChoiceSelect(key, choice)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleChoiceSelect(key, choice);
                }
              }}
            >
              {choice.icon && (
                <span
                  className={`${baseClasses.icon} ${dynamicClasses[`choiceIcon-${key}`] || ''}`}
                >
                  {choice.icon}
                </span>
              )}
              <Typography
                className={`${baseClasses.choiceText} ${dynamicClasses[`choiceText-${key}`] || ''}`}
              >
                {parsedText}
              </Typography>
            </div>
          );
        })}
      </div>
    </Fade>
  );
};

export default ChoiceSelector;
